import React from "react"
import type { ReactNode } from "react"

export const arrayToColumns = <T,>(arr: T[], cols: number): T[][] => {
  const chunks: T[][] = new Array(cols)
  for (let i = 0; i < arr.length; ++i) {
    const index = i % cols
    if (!chunks[index]) {
      chunks[index] = []
    }
    chunks[index].push(arr[i])
  }
  return chunks
}

type Gap = 0 | 1 | 2 | 4

const gapClasses: Record<Gap, string> = {
  0: "gap-0",
  1: "gap-1",
  2: "gap-2",
  4: "gap-4"
}

const mtClasses: Record<Gap, string> = {
  0: "mt-0",
  1: "mt-1",
  2: "mt-2",
  4: "mt-4"
}

export type MasonryProps = {
  /** Number of columns in the masonry grid */
  columns: number
  /** Gap between grid items */
  gap?: Gap
  /** Grid content */
  children: ReactNode
}

/**
 * Distributes children into a CSS grid-based masonry layout with configurable
 * columns and gap spacing.
 */
const Masonry = React.forwardRef<HTMLDivElement, MasonryProps>(
  ({ columns, gap = 1, children }, ref) => {
    const childrenArray = React.Children.toArray(children)
    const transformed = arrayToColumns(childrenArray, columns)
    const classes = `grid ${gapClasses[gap]}`
    const styles = {
      gridTemplateColumns: `repeat(${columns}, 1fr)`
    }
    return (
      <div ref={ref} className={classes} style={styles}>
        {transformed.map((elements, key) => (
          <div key={key}>
            {React.Children.map(elements, (element, index) => {
              const topMarginClass = index === 0 ? "mt-0" : mtClasses[gap]
              return <div className={`mb-0 ${topMarginClass}`}>{element}</div>
            })}
          </div>
        ))}
      </div>
    )
  }
)

export { Masonry }
export default Masonry
